import type {
  ChipColor,
  ColoredToken,
  CspDirectives,
  EvaluationFinding,
  IChipColorizer,
  IClipboardService,
  ICspEvaluator,
  ICspExporter,
  ICspGenerator,
  ICspParser,
  ICspQuiz,
  ICspReportExporter,
  ICspTemplateService,
  ICspUrlMatcher,
  ICspValidator,
  IUrlStateManager,
  QuizAnswers,
  QuizQuestion,
  ReportData,
  ResourceType,
  UrlMatchResult,
} from '../core/types';
import { CSP_KEYWORDS, RESOURCE_TYPE_OPTIONS } from '../core/types';
import type { FetchedPolicy, ICspClipperService } from '../services/CspClipperService';

/** Text colors for each value category in the highlighted policy. */
const VALUE_TOKEN_CLASSES: Record<ChipColor['category'], string> = {
  keyword: 'text-purple-700 dark:text-purple-400',
  nonce: 'text-teal-700 dark:text-teal-400',
  hash: 'text-emerald-700 dark:text-emerald-400',
  scheme: 'text-amber-700 dark:text-amber-400',
  domain: 'text-blue-700 dark:text-blue-400',
  wildcard: 'text-red-700 dark:text-red-400',
};
const DIRECTIVE_TOKEN_CLASSES = 'text-indigo-700 dark:text-indigo-300 font-semibold';
const SEPARATOR_TOKEN_CLASSES = 'text-slate-400 dark:text-slate-500';

/**
 * Main Alpine.js component for the Visual CSP Editor.
 *
 * Dependency Inversion: all dependencies are injected via constructor,
 * making this component testable and decoupled from implementations.
 *
 * Single Responsibility: orchestrates the UI state and user interactions.
 * All heavy logic is delegated to injected services.
 */
export class EditorApp {
  constructor(
    private parser: ICspParser,
    private generator: ICspGenerator,
    private evaluator: ICspEvaluator,
    private urlState: IUrlStateManager,
    private clipboard: IClipboardService,
    private colorizer: IChipColorizer,
    private validator: ICspValidator,
    private templateService: ICspTemplateService,
    private reportExporter: ICspReportExporter,
    private cspExporter: ICspExporter,
    private quiz: ICspQuiz,
    private cspClipper: ICspClipperService,
    private urlMatcher: ICspUrlMatcher
  ) {}

  /**
   * Creates the Alpine.js data object for x-data binding.
   */
  createAlpineData() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const app = this;

    return {
      // State
      rawCsp: '',
      directives: {} as CspDirectives,
      newDirectiveName: '',
      copyText: 'Copy CSP Text',
      shareText: 'Copy Shareable Link',
      projectName: '',
      projectUrl: '',
      findings: [] as EvaluationFinding[],
      darkMode: false,
      directiveWarning: '' as string,
      valueWarnings: {} as Record<string, string>,
      rawCspWarning: '' as string,
      collapsedDirectives: {} as Record<string, boolean>,
      templates: app.templateService.getTemplates(),
      showTemplates: false,
      // Quiz state
      showQuiz: false,
      quizQuestions: app.quiz.getQuestions() as QuizQuestion[],
      quizStep: 0,
      quizAnswers: {} as QuizAnswers,
      directiveFilter: '',
      draggedDirective: null as string | null,
      dragOverDirective: null as string | null,
      dragDirection: 'below' as 'above' | 'below',
      showToast: false,
      toastMessage: '',
      toastIcon: 'success' as 'success' | 'error' | 'info',
      fetchUrl: '',
      fetchLoading: false,
      fetchError: '',
      fetchedPolicies: [] as FetchedPolicy[],
      activePolicyIndex: 0,
      // URL tester state
      resourceTypes: RESOURCE_TYPE_OPTIONS,
      urlTestInput: '',
      urlTestType: 'script' as ResourceType,
      urlTestResult: null as UrlMatchResult | null,
      urlTestError: '',

      // Alpine lifecycle
      init() {
        // Dark mode: check localStorage, then system preference
        const stored = localStorage.getItem('darkMode');
        if (stored !== null) {
          this.darkMode = stored === 'true';
        } else if (typeof window.matchMedia === 'function') {
          this.darkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
        }
        this.applyDarkMode();

        const state = app.urlState.load();
        if (state) {
          this.projectName = state.projectName;
          this.projectUrl = state.projectUrl;
          this.rawCsp = state.rawCsp;
          if (this.rawCsp) {
            this.parseCsp();
          }
        }
      },

      toggleDarkMode() {
        this.darkMode = !this.darkMode;
        localStorage.setItem('darkMode', String(this.darkMode));
        this.applyDarkMode();
      },

      applyDarkMode() {
        document.documentElement.classList.toggle('dark', this.darkMode);
      },

      displayToast(message: string, icon: 'success' | 'error' | 'info' = 'success') {
        this.toastMessage = message;
        this.toastIcon = icon;
        this.showToast = true;
        setTimeout(() => {
          this.showToast = false;
        }, 3000);
      },

      // --- URL State ---
      updateUrl() {
        app.urlState.save({
          directives: this.directives,
          projectName: this.projectName,
          projectUrl: this.projectUrl,
          rawCsp: this.rawCsp,
        });
        this.evaluateFindings();
        this.refreshUrlTest();
      },

      // --- CSP Logic ---
      parseCsp() {
        const parsed = app.parser.parse(this.rawCsp);
        const cleanDirectives: CspDirectives = {};
        const warnings: string[] = app.parser.diagnose(this.rawCsp);
        
        for (const [directive, values] of Object.entries(parsed)) {
          // Validate directive name
          const dirResult = app.validator.validateDirective(directive);
          if (!dirResult.valid) {
            warnings.push(`Directive "${directive}": ${dirResult.warning}`);
            continue; // Skip invalid directive completely
          }
          if (dirResult.warning) {
            warnings.push(`Directive "${directive}": ${dirResult.warning}`);
          }
          
          // Validate and filter values
          const validValues: string[] = [];
          for (const value of values) {
            const valResult = app.validator.validateValue(value, directive);
            if (!valResult.valid) {
              warnings.push(`Value "${value}" in ${directive}: ${valResult.warning}`);
              // Don't add invalid value
            } else {
              validValues.push(value);
              if (valResult.warning) {
                warnings.push(`Value "${value}" in ${directive}: ${valResult.warning}`);
              }
            }
          }
          
          // Only add directive if it has valid values or is a boolean directive
          if (validValues.length > 0 || directive === 'upgrade-insecure-requests' || directive === 'block-all-mixed-content') {
            cleanDirectives[directive] = validValues;
          }
        }
        
        this.directives = cleanDirectives;
        
        // Show consolidated warning if there were issues
        if (warnings.length > 0) {
          const preview = warnings.slice(0, 3).join(' • ');
          const more = warnings.length > 3 ? ` (${warnings.length - 3} more issues)` : '';
          this.rawCspWarning = `Found ${warnings.length} issue(s): ${preview}${more}`;
        } else {
          this.rawCspWarning = '';
        }
        
        this.updateUrl();
      },

      async fetchFromUrl() {
        const url = this.fetchUrl.trim();
        if (!url) return;

        this.fetchError = '';
        this.fetchLoading = true;

        try {
          const result = await app.cspClipper.fetchCsp(url);
          // Older clipper versions only return a single `csp` string.
          const policies: FetchedPolicy[] = result.policies?.length
            ? result.policies
            : result.csp
              ? [{ policy: result.csp, reportOnly: !!result.reportOnly }]
              : [];

          if (policies.length > 0) {
            if (result.title) this.projectName = result.title;
            this.projectUrl = url;
            this.fetchedPolicies = policies;
            // Start with the first enforced policy, since that is what browsers block with.
            const firstEnforced = policies.findIndex((p) => !p.reportOnly);
            this.loadFetchedPolicy(firstEnforced === -1 ? 0 : firstEnforced);
            this.displayToast('CSP loaded successfully from URL', 'success');
          } else {
            this.fetchError = 'No CSP header found on this URL.';
          }
        } catch (err) {
          this.fetchError = err instanceof Error ? err.message : 'Failed to fetch CSP.';
        } finally {
          this.fetchLoading = false;
        }
      },

      loadFetchedPolicy(index: number) {
        const fetched = this.fetchedPolicies[index];
        if (!fetched) return;
        this.activePolicyIndex = index;
        this.rawCsp = fetched.policy;
        this.parseCsp();
      },

      isActivePolicyReportOnly(): boolean {
        return !!this.fetchedPolicies[this.activePolicyIndex]?.reportOnly;
      },

      getFetchedPolicyLabel(index: number): string {
        const fetched = this.fetchedPolicies[index];
        if (!fetched) return '';
        return `Policy ${index + 1} (${fetched.reportOnly ? 'report-only' : 'enforced'})`;
      },

      clearFetchedPolicies() {
        this.fetchedPolicies = [];
        this.activePolicyIndex = 0;
      },

      validateRawCsp() {
        const raw = this.rawCsp.trim();
        if (!raw) {
          this.rawCspWarning = '';
          return;
        }

        // Check for obvious non-CSP content
        if (!/[a-z-]+/.test(raw)) {
          this.rawCspWarning = 'This doesn\'t look like a valid CSP. Expected format: "directive-name value1 value2; another-directive value"';
          return;
        }

        // Check if it looks like random text
        const parts = raw.split(/[;\s]+/).filter(Boolean);
        const validDirectivePattern = /^[a-z][a-z0-9-]*$/;
        const hasValidDirective = parts.some(part => validDirectivePattern.test(part));

        if (!hasValidDirective) {
          this.rawCspWarning = 'No valid CSP directive found. Directives should look like: default-src, script-src, style-src, etc.';
          return;
        }

        // Try to parse and see if we get empty result
        try {
          const parsed = app.parser.parse(raw);
          if (Object.keys(parsed).length === 0) {
            this.rawCspWarning = 'Could not parse any directives from this text. Check the format: "directive-name value1 value2"';
            return;
          }
        } catch {
          this.rawCspWarning = 'Error parsing CSP. Make sure the format is correct.';
          return;
        }

        this.rawCspWarning = '';
      },

      generateCsp(): string {
        return app.generator.generate(this.directives);
      },

      getCspLength(): number {
        return this.generateCsp().length;
      },

      getCspLengthColor(): string {
        const length = this.getCspLength();
        if (length < 2048) {
          return 'text-emerald-400';
        } else if (length < 4096) {
          return 'text-amber-400';
        } else {
          return 'text-red-400';
        }
      },

      addValue(directive: string, event: KeyboardEvent) {
        event.preventDefault();
        const input = event.target as HTMLInputElement;
        let val = input.value.trim();

        if (!val) {
          input.value = '';
          return;
        }

        // Auto-quote known keywords
        if (CSP_KEYWORDS.includes(val.toLowerCase() as typeof CSP_KEYWORDS[number])) {
          val = `'${val.toLowerCase()}'`;
        }

        // Validate the value
        const result = app.validator.validateValue(val, directive);

        if (!result.valid) {
          // Force Alpine reactivity by creating new object
          const updated = { ...this.valueWarnings };
          updated[directive] = result.warning || 'Invalid value.';
          this.valueWarnings = updated;
          this.clearWarning('value', directive);
          input.value = '';
          return;
        }

        if (result.warning) {
          const updated = { ...this.valueWarnings };
          updated[directive] = result.warning;
          this.valueWarnings = updated;
          this.clearWarning('value', directive);
        } else {
          // Clear any existing warning
          const updated = { ...this.valueWarnings };
          delete updated[directive];
          this.valueWarnings = updated;
        }

        if (!this.directives[directive].includes(val)) {
          this.directives[directive].push(val);
          this.updateUrl();
        }
        input.value = '';
      },

      removeValue(directive: string, index: number) {
        this.directives[directive].splice(index, 1);
        this.updateUrl();
      },

      addDirective() {
        const name = this.newDirectiveName.trim().toLowerCase();
        if (!name) {
          this.newDirectiveName = '';
          return;
        }

        const result = app.validator.validateDirective(name);

        if (!result.valid) {
          this.directiveWarning = result.warning || 'Invalid directive name.';
          this.clearWarning('directive');
          this.newDirectiveName = '';
          return;
        }

        if (result.warning) {
          this.directiveWarning = result.warning;
          this.clearWarning('directive');
        }

        if (!this.directives[name]) {
          this.directives[name] = [];
          this.updateUrl();
        }
        this.newDirectiveName = '';
      },

      removeDirective(directive: string) {
        delete this.directives[directive];
        delete this.collapsedDirectives[directive];
        this.updateUrl();
      },

      toggleCollapse(directive: string) {
        this.collapsedDirectives[directive] = !this.collapsedDirectives[directive];
      },

      isCollapsed(directive: string): boolean {
        return !!this.collapsedDirectives[directive];
      },

      // --- Filter ---
      get filteredDirectives(): CspDirectives {
        if (!this.directiveFilter.trim()) {
          return this.directives;
        }

        const filter = this.directiveFilter.toLowerCase().trim();
        const filtered: CspDirectives = {};

        for (const [directive, values] of Object.entries(this.directives)) {
          // Match directive name
          if (directive.toLowerCase().includes(filter)) {
            filtered[directive] = values;
            continue;
          }

          // Match values
          const hasMatchingValue = values.some((value) =>
            value.toLowerCase().includes(filter)
          );
          if (hasMatchingValue) {
            filtered[directive] = values;
          }
        }

        return filtered;
      },

      getFilteredCount(): { shown: number; total: number } {
        return {
          shown: Object.keys(this.filteredDirectives).length,
          total: Object.keys(this.directives).length,
        };
      },

      clearFilter() {
        this.directiveFilter = '';
      },

      // --- Drag and Drop Reordering ---
      getDirectiveKeys(): string[] {
        return Object.keys(this.directives);
      },

      onDragStart(directive: string) {
        this.draggedDirective = directive;
      },

      onDragEnd() {
        this.draggedDirective = null;
        this.dragOverDirective = null;
      },

      onDragOver(directive: string) {
        this.dragOverDirective = directive;
        
        // Calculate drag direction based on indices
        if (this.draggedDirective && this.draggedDirective !== directive) {
          const keys = this.getDirectiveKeys();
          const fromIndex = keys.indexOf(this.draggedDirective);
          const toIndex = keys.indexOf(directive);
          
          // If dragging downward (from smaller to larger index), show line below
          // If dragging upward (from larger to smaller index), show line above
          this.dragDirection = fromIndex < toIndex ? 'below' : 'above';
        }
      },

      onDragLeave() {
        this.dragOverDirective = null;
      },

      onDrop(targetDirective: string) {
        if (!this.draggedDirective || this.draggedDirective === targetDirective) {
          this.draggedDirective = null;
          this.dragOverDirective = null;
          return;
        }

        const keys = this.getDirectiveKeys();
        const fromIndex = keys.indexOf(this.draggedDirective);
        const toIndex = keys.indexOf(targetDirective);

        if (fromIndex === -1 || toIndex === -1) {
          this.draggedDirective = null;
          this.dragOverDirective = null;
          return;
        }

        this.moveDirective(fromIndex, toIndex);
        this.draggedDirective = null;
        this.dragOverDirective = null;
      },

      moveDirective(fromIndex: number, toIndex: number) {
        const keys = this.getDirectiveKeys();
        const newDirectives: CspDirectives = {};

        // Reorder keys
        const reorderedKeys = [...keys];
        const [movedKey] = reorderedKeys.splice(fromIndex, 1);
        reorderedKeys.splice(toIndex, 0, movedKey);

        // Rebuild directives object with new order
        for (const key of reorderedKeys) {
          newDirectives[key] = this.directives[key];
        }

        this.directives = newDirectives;
        this.updateUrl();
      },

      moveDirectiveUp(directive: string) {
        const keys = this.getDirectiveKeys();
        const index = keys.indexOf(directive);
        if (index > 0) {
          this.moveDirective(index, index - 1);
        }
      },

      moveDirectiveDown(directive: string) {
        const keys = this.getDirectiveKeys();
        const index = keys.indexOf(directive);
        if (index < keys.length - 1 && index !== -1) {
          this.moveDirective(index, index + 1);
        }
      },

      canMoveUp(directive: string): boolean {
        const keys = this.getDirectiveKeys();
        return keys.indexOf(directive) > 0;
      },

      canMoveDown(directive: string): boolean {
        const keys = this.getDirectiveKeys();
        const index = keys.indexOf(directive);
        return index !== -1 && index < keys.length - 1;
      },

      isDragging(directive: string): boolean {
        return this.draggedDirective === directive;
      },

      isDragOver(directive: string): boolean {
        return this.dragOverDirective === directive && this.draggedDirective !== directive;
      },

      isDragOverAbove(directive: string): boolean {
        return this.isDragOver(directive) && this.dragDirection === 'above';
      },

      isDragOverBelow(directive: string): boolean {
        return this.isDragOver(directive) && this.dragDirection === 'below';
      },

      resetEditor() {
        this.directives = {};
        this.rawCsp = '';
        this.projectName = '';
        this.projectUrl = '';
        this.findings = [];
        this.directiveWarning = '';
        this.valueWarnings = {};
        this.rawCspWarning = '';
        this.directiveFilter = '';
        this.urlTestResult = null;
        this.urlTestError = '';
        this.clearFetchedPolicies();
        this.updateUrl();
      },

      applyTemplate(templateId: string) {
        const policy = app.templateService.applyTemplate(templateId);
        if (policy) {
          this.directives = policy;
          this.rawCsp = '';
          this.showTemplates = false;
          this.clearFetchedPolicies();
          this.updateUrl();
        }
      },

      toggleTemplates() {
        this.showTemplates = !this.showTemplates;
        this.showQuiz = false;
      },

      // --- Quiz ---
      startQuiz() {
        this.showTemplates = false;
        this.showQuiz = true;
        this.quizStep = 0;
        this.quizAnswers = {};
      },

      // Single-choice: set answer and auto-advance skipping skipIf questions.
      // Multi-select: toggle the value in the array (no auto-advance).
      answerQuiz(questionId: string, value: string) {
        const question = this.quizQuestions.find((q: QuizQuestion) => q.id === questionId);
        if (!question) return;

        if (question.type === 'multi') {
          this.toggleMultiAnswer(questionId, value);
        } else {
          this.quizAnswers[questionId] = value;
          this.advanceQuiz();
        }
      },

      toggleMultiAnswer(questionId: string, value: string) {
        const current = this.quizAnswers[questionId];
        const arr: string[] = Array.isArray(current) ? [...current] : [];
        const idx = arr.indexOf(value);
        if (idx >= 0) {
          arr.splice(idx, 1);
        } else {
          arr.push(value);
        }
        this.quizAnswers[questionId] = arr;
      },

      isQuizOptionSelected(questionId: string, value: string): boolean {
        const answer = this.quizAnswers[questionId];
        return Array.isArray(answer) ? answer.includes(value) : answer === value;
      },

      // Advance to the next non-skipped question, or finish if none remain.
      advanceQuiz() {
        let next = this.quizStep + 1;
        while (next < this.quizQuestions.length) {
          if (!this.quizQuestions[next].skipIf?.(this.quizAnswers)) break;
          next++;
        }
        if (next >= this.quizQuestions.length) {
          this.applyQuizResults();
        } else {
          this.quizStep = next;
        }
      },

      // Explicit advance for multi questions (called by "Next" button).
      proceedQuiz() {
        this.advanceQuiz();
      },

      quizBack() {
        let prev = this.quizStep - 1;
        while (prev > 0 && this.quizQuestions[prev].skipIf?.(this.quizAnswers)) {
          prev--;
        }
        if (prev >= 0) {
          this.quizStep = prev;
        }
      },

      cancelQuiz() {
        this.showQuiz = false;
        this.quizStep = 0;
        this.quizAnswers = {};
      },

      applyQuizResults() {
        const policy = app.quiz.generatePolicy(this.quizAnswers);
        this.directives = policy;
        this.rawCsp = '';
        this.clearFetchedPolicies();
        this.showQuiz = false;
        this.quizStep = 0;
        this.quizAnswers = {};
        this.updateUrl();
        this.displayToast('CSP generated from quiz!', 'success');
      },

      get quizProgress(): number {
        return this.quizQuestions.length > 0
          ? Math.round(((this.quizStep + 1) / this.quizQuestions.length) * 100)
          : 0;
      },

      clearWarning(type: 'directive' | 'value', directive?: string) {
        setTimeout(() => {
          if (type === 'directive') {
            this.directiveWarning = '';
          } else if (directive) {
            const updated = { ...this.valueWarnings };
            delete updated[directive];
            this.valueWarnings = updated;
          }
        }, 5000);
      },

      // --- Clipboard ---
      async copyToClipboard() {
        const success = await app.clipboard.copyText(this.generateCsp());
        if (success) {
          this.displayToast('Policy copied to clipboard!', 'success');
        }
      },

      async copyShareLink() {
        const success = await app.clipboard.copyText(window.location.href);
        if (success) {
          this.displayToast('Link copied to clipboard!', 'success');
        }
      },

      // --- Visual ---
      getChipColor(val: string): string {
        return app.colorizer.getColor(val).classes;
      },

      /**
       * Syntax-highlighted policy as plain-text tokens. Rendered with x-text,
       * never as HTML, so values from shared links or fetched headers cannot inject markup.
       */
      getColoredCspTokens(): ColoredToken[] {
        const tokens: ColoredToken[] = [];
        Object.entries(this.directives).forEach(([name, values], index) => {
          if (index > 0) {
            tokens.push({ text: ';', classes: SEPARATOR_TOKEN_CLASSES }, { text: ' ', classes: '' });
          }
          tokens.push({ text: name, classes: DIRECTIVE_TOKEN_CLASSES });
          for (const value of values) {
            const { category } = app.colorizer.getColor(value);
            tokens.push({ text: ' ', classes: '' }, { text: value, classes: VALUE_TOKEN_CLASSES[category] });
          }
        });
        return tokens;
      },

      // --- Security Evaluation ---
      evaluateFindings() {
        const csp = this.generateCsp();
        this.findings = app.evaluator.evaluate(csp);
      },

      // --- URL Tester ---
      testUrl() {
        this.urlTestError = '';
        try {
          this.urlTestResult = app.urlMatcher.match(
            this.directives,
            this.urlTestInput,
            this.urlTestType,
            this.projectUrl
          );
        } catch (err) {
          this.urlTestResult = null;
          this.urlTestError = err instanceof Error ? err.message : 'Could not test this URL.';
        }
      },

      // Keep a shown result in sync while the policy is edited.
      refreshUrlTest() {
        if (this.urlTestResult) {
          this.testUrl();
        }
      },

      getSeverityColor(finding: EvaluationFinding): string {
        switch (finding.severityLabel) {
          case 'High':
            return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800';
          case 'Medium':
            return 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800';
          case 'High (Possible)':
            return 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800';
          case 'Medium (Possible)':
            return 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-900/20 dark:text-orange-400 dark:border-orange-800';
          case 'Syntax':
            return 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-800';
          case 'Info':
            return 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800';
          default:
            return 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700';
        }
      },

      // --- Report Export ---
      exportAsMarkdown() {
        const reportData = this.buildReportData();
        app.reportExporter.downloadMarkdown(reportData);
      },

      exportAsJson() {
        const reportData = this.buildReportData();
        app.reportExporter.downloadJson(reportData);
      },

      buildReportData(): ReportData {
        return {
          projectName: this.projectName,
          projectUrl: this.projectUrl,
          generatedAt: new Date().toISOString(),
          directives: this.directives,
          findings: this.findings,
          rawCsp: this.generateCsp(),
        };
      },

      // --- Server Config Export ---
      downloadAsExpress() {
        const csp = this.generateCsp();
        app.cspExporter.downloadAsExpress(csp);
      },

      downloadAsCloudflare() {
        const csp = this.generateCsp();
        app.cspExporter.downloadAsCloudflare(csp);
      },

      downloadAsNginx() {
        const csp = this.generateCsp();
        app.cspExporter.downloadAsNginx(csp);
      },

      downloadAsApache() {
        const csp = this.generateCsp();
        app.cspExporter.downloadAsApache(csp);
      },

      downloadAsHtmlMeta() {
        const csp = this.generateCsp();
        app.cspExporter.downloadAsHtml(csp);
      },
    };
  }
}
